import { PlayCircleIcon, PlusIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import youtubeClientUtil from '../../clientUtils/youtubeClientUtil';
import firebaseStore from '../../stores/firebaseStore';
import modalStore from '../../stores/modalStore';
import Frame from '../../uikit/Frame';
import CreateLessonModalContent from '../lesson/modal/CreateLessonModalContent';
import DiscoverList from './DiscoverList';
import UserLessonList from './UserLessonList';

class ViewStore {
    // 로딩 중 | 로그인 필요 | 로그인됨
    state: 'loading' | 'login_required' | 'logged_in' = 'loading';
    youtubeUrl: string = '';
    extractedVideoId: string | null = null;
    validationError: string | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    async checkAuth() {
        try {
            const isLoggedIn = await firebaseStore.isLoggedIn();
            runInAction(() => {
                this.state = isLoggedIn ? 'logged_in' : 'login_required';
            });
        } catch {
            runInAction(() => {
                this.state = 'login_required';
            });
        }
    }

    setYoutubeUrl(url: string) {
        this.youtubeUrl = url;

        // videoId 추출 시도
        const videoId = youtubeClientUtil.extractVideoId(url);

        if (videoId) {
            this.extractedVideoId = videoId;
            this.validationError = null;
        } else {
            this.extractedVideoId = null;
            this.validationError = '유효한 유튜브 URL 또는 비디오 ID를 입력해주세요';
        }
    }

    resetYoutubeUrl() {
        this.youtubeUrl = '';
        this.extractedVideoId = null;
        this.validationError = null;
    }

    get isValidInput(): boolean {
        return this.extractedVideoId !== null;
    }

    async handleCreateLesson() {
        if (!this.extractedVideoId) {
            return;
        }

        // 모달 열기
        const result = await modalStore.custom({
            title: '레슨 만들기',
            renderComponent: ({ modalId }) => (
                <CreateLessonModalContent modalId={modalId} videoId={this.extractedVideoId!} />
            ),
            showCloseButton: true,
            preventClose: true,
        });

        if (result.returnValue) {
            console.log('레슨 생성 완료:', result.returnValue);
            // TODO: 실제 레슨 생성 처리
        }
    }
}

const ListeningDashboardPage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        viewStore.checkAuth();
    }, [viewStore]);

    const onClickCreateLesson = async () => {
        if (!viewStore.extractedVideoId) {
            return;
        }

        // 모달 열기
        await modalStore.custom({
            title: '레슨 만들기',
            renderComponent: ({ modalId }) => (
                <CreateLessonModalContent modalId={modalId} videoId={viewStore.extractedVideoId!} />
            ),
            showCloseButton: true,
            preventClose: true,
        });
    };

    return (
        <Frame className="bg-base-100 gap-0">
            {/* 메인 컨텐츠 영역 - 최대 너비 제한 */}
            <div className="mx-auto w-full max-w-[880px] px-4 pb-10">
                {/* 중앙 입력 영역 */}
                <div className="flex flex-col items-center pt-20 pb-10">
                    <h1 className="text-base-content mb-8 text-2xl font-normal">
                        좋아하는 영상으로 영어 듣기 레슨을 만들어보세요.
                    </h1>
                    <div
                        className={twMerge(
                            'border-base-content/30 flex w-full cursor-text items-center gap-3 rounded-full border py-2 pr-2 pl-4 shadow-sm',
                            'max-w-[700px]', // input 은 최대 너비 제한
                            'focus-within:border-base-content/50 transition-colors',
                            viewStore.validationError &&
                                viewStore.youtubeUrl &&
                                'border-error/50 focus-within:border-error/70',
                        )}
                        onClick={() => inputRef.current?.focus()}
                    >
                        <PlayCircleIcon
                            className={twMerge(
                                'stroke-base-content/20 size-8 shrink-0 stroke-1',
                                viewStore.isValidInput && 'stroke-base-content/60',
                            )}
                        />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="유튜브 URL 또는 비디오 ID"
                            className="text-base-content placeholder:text-base-content/40 min-w-0 flex-1 bg-transparent text-base outline-none"
                            value={viewStore.youtubeUrl}
                            onChange={(e) => {
                                const url = e.target.value;

                                if (!url.trim()) {
                                    viewStore.resetYoutubeUrl();
                                } else {
                                    viewStore.setYoutubeUrl(url);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && viewStore.isValidInput) {
                                    onClickCreateLesson();
                                }
                            }}
                        />
                        <button
                            className={twMerge(
                                'btn btn-primary flex items-center justify-center rounded-full px-5',
                                !viewStore.isValidInput && 'btn-soft',
                            )}
                            onClick={() => {
                                viewStore.setYoutubeUrl(inputRef.current?.value || '');

                                if (!viewStore.isValidInput) {
                                    return;
                                }

                                if (viewStore.state !== 'logged_in') {
                                    return;
                                }

                                onClickCreateLesson();
                            }}
                        >
                            <PlusIcon className="size-4.5" />
                            레슨 만들기
                        </button>
                    </div>
                    {/* 에러 메시지 */}
                    <p className="text-error mt-2 min-h-6 text-sm">{viewStore.validationError}</p>
                </div>

                {/* 내 레슨 섹션 */}
                <section className="mt-2">
                    <h2 className="text-base-content mb-3 text-lg font-medium">내 레슨</h2>
                    <UserLessonList />
                </section>

                {/* 발견 섹션 */}
                <section className="mt-20">
                    <h2 className="text-base-content mb-1 text-lg font-medium">
                        다른 레슨 살펴보기
                    </h2>
                    <p className="text-base-content/50 mb-4 text-sm">
                        레슨을 선택해서 바로 시작할 수 있어요
                    </p>
                    <DiscoverList />
                </section>
            </div>

            <Frame.Spacer />
        </Frame>
    );
});

export default ListeningDashboardPage;
